/**
 * Event-log emitter.
 *
 * Module-level singleton — components just call `logEvent(type, meta)`.
 * Events are queued in memory, mirrored to localStorage (so a crash or
 * tab-close doesn't lose them), and flushed to /api/events/batch every
 * 5s, every 25 events, or on `pagehide`.
 *
 * Failures retry with exponential backoff up to 3 attempts, then drop
 * silently. Telemetry must never break the app.
 *
 * Auth-aware: if no token is present yet, queues continue to accumulate
 * and are flushed once the user logs in. If the backend returns 401 we
 * pause flushing until the next token arrives.
 */
import { api } from '../services/api';

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_AT_SIZE     = 25;
const MAX_QUEUE         = 500;          // hard cap so a broken backend can't OOM the tab
const STORAGE_KEY       = 'aiquire_event_queue_v1';
const SESSION_KEY       = 'aiquire_event_session_v1';
const RETRY_BASE_MS     = 1500;
const RETRY_MAX_ATTEMPTS = 3;

let queue = [];
let timer = null;
let inFlight = false;
let consecutiveFailures = 0;
let pausedUntilAuth = false;

// Stable browser-session id (rotates per browser session, not per tab).
const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto.randomUUID && crypto.randomUUID()) ||
            `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
};

const loadPersistedQueue = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) queue = parsed.slice(0, MAX_QUEUE);
    }
  } catch { /* ignore */ }
};

const persistQueue = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue.slice(0, MAX_QUEUE)));
  } catch { /* quota or private-browsing — ok */ }
};

const hasAuth = () => {
  try { return !!localStorage.getItem('authToken'); }
  catch { return false; }
};

const scheduleFlush = () => {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flush().catch(() => {});
  }, FLUSH_INTERVAL_MS);
};

const flush = async ({ keepalive = false } = {}) => {
  if (inFlight) return;
  if (queue.length === 0) return;
  if (!hasAuth()) {
    // No token yet — let events accumulate; we'll try again later.
    return;
  }
  if (pausedUntilAuth) {
    // Last flush hit 401. Wait for the auth token to rotate.
    if (!hasAuth()) return;
    pausedUntilAuth = false;
  }

  const batch = queue.slice(0, FLUSH_AT_SIZE * 2); // up to 50 per round
  inFlight = true;
  try {
    const headers = {
      'X-Session-Id': getSessionId() || '',
    };
    if (typeof window !== 'undefined') {
      headers['X-Viewport-W'] = String(window.innerWidth || 0);
      headers['X-Viewport-H'] = String(window.innerHeight || 0);
    }
    await api.post(
      '/events/batch',
      { events: batch },
      { headers, timeout: 8000 },
    );
    // Success — remove sent items from the queue and persist
    queue = queue.slice(batch.length);
    persistQueue();
    consecutiveFailures = 0;
    if (queue.length > 0) scheduleFlush();
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) {
      // Auth-blocked — pause until token rotates
      pausedUntilAuth = true;
      return;
    }
    consecutiveFailures += 1;
    if (consecutiveFailures >= RETRY_MAX_ATTEMPTS) {
      // Drop this batch so we don't loop forever on a poisoned event
      console.warn(`[eventLog] dropping ${batch.length} events after ${RETRY_MAX_ATTEMPTS} failures`);
      queue = queue.slice(batch.length);
      persistQueue();
      consecutiveFailures = 0;
      if (queue.length > 0) scheduleFlush();
      return;
    }
    // Exponential backoff
    const delay = RETRY_BASE_MS * Math.pow(2, consecutiveFailures - 1);
    setTimeout(() => { flush().catch(() => {}); }, delay);
  } finally {
    inFlight = false;
  }
};

const flushBeacon = () => {
  // Fire-and-forget on pagehide via sendBeacon. No auth header possible,
  // so we only use this as a last resort — the next page load will still
  // pick up the queue from localStorage and flush properly.
  if (queue.length === 0) return;
  persistQueue();
};

// ───────── Public API ─────────

export const logEvent = (type, opts = {}) => {
  if (!type) return;
  const ev = {
    type,
    tender_id: opts.tender_id || null,
    page: opts.page || (typeof window !== 'undefined' ? window.location.pathname : null),
    referrer: opts.referrer || (typeof document !== 'undefined' ? document.referrer : null) || null,
    outcome: opts.outcome || 'success',
    latency_ms: opts.latency_ms ?? null,
    meta: opts.meta || null,
    ts: new Date().toISOString(),
  };
  queue.push(ev);
  if (queue.length > MAX_QUEUE) {
    queue = queue.slice(-MAX_QUEUE); // drop oldest
  }
  persistQueue();
  if (queue.length >= FLUSH_AT_SIZE) {
    flush().catch(() => {});
  } else {
    scheduleFlush();
  }
};

export const flushEvents = () => flush();

// Initialize once per page load
let initialized = false;
export const initEventLog = () => {
  if (initialized) return;
  initialized = true;
  loadPersistedQueue();
  // Flush whatever was buffered from the previous session
  if (queue.length > 0) scheduleFlush();
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushBeacon);
    window.addEventListener('beforeunload', flushBeacon);
    // Best-effort: when auth changes, retry
    window.addEventListener('storage', (e) => {
      if (e.key === 'authToken' && e.newValue) {
        pausedUntilAuth = false;
        if (queue.length > 0) flush().catch(() => {});
      }
    });
  }
};
